import { useEffect, useState, type CSSProperties } from "react";
import "./regie.css";

/**
 * L'écran central d'un stand (Espaces Humains) : la marque, le QR code de
 * l'événement, et les rendez-vous à venir — sujet, heure, lieu, places — en
 * direct. On l'ouvre depuis la régie sur l'ordinateur branché à l'écran, puis
 * double-clic (plein écran).
 *
 * Il ne lit que ce que voit déjà un visiteur : aucune donnée de régie, aucun
 * jeton sur un ordinateur de salon. Les échanges restent privés : seuls les
 * rendez-vous sont montrés.
 */

interface TablePublique {
  sessionId: string;
  nom: string;
  sujet: string;
  point: string;
  rdvAt: number;
  couleur: string;
  participants: number;
  maximum: number;
  phase?: string;
}

interface EvenementPublic {
  id: string;
  nom: string;
  lieu?: string;
  fin: number;
  voix: { nom: string; image: string | null; phrase: string; couleur?: string | null } | null;
  tables: TablePublique[];
}

/** Le violet d'Espaces Humains, quand la marque n'a pas choisi sa couleur. */
const VIOLET = "#7c5cff";

const heure = (ms: number): string =>
  new Date(ms).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }).replace(":", " h ");

/** Où en est une table, en deux ou trois mots. */
function etat(t: TablePublique, now: number): { texte: string; vif: boolean } {
  const places = Math.max(0, t.maximum - t.participants);
  switch (t.phase) {
    case "chat":
      return { texte: places > 0 ? `En cours · ${places} ${places > 1 ? "places" : "place"}` : "En cours", vif: true };
    case "rendez-vous":
      return { texte: "Rendez-vous maintenant", vif: true };
    case "réunie":
      return { texte: "Réunis", vif: false };
    default: {
      const dans = Math.round((t.rdvAt - now) / 60_000);
      const quand = dans <= 0 ? "" : dans < 60 ? ` · dans ${dans} min` : "";
      return { texte: `${places} ${places > 1 ? "places" : "place"}${quand}`, vif: false };
    }
  }
}

function ordre(t: TablePublique): number {
  return t.phase === "réunie" ? 1 : 0;
}

export function Projection(): JSX.Element {
  const id = new URLSearchParams(location.search).get("evt") ?? "";
  const [e, setE] = useState<EvenementPublic | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let vivant = true;
    const lire = async (): Promise<void> => {
      try {
        const r = await fetch(`/api/evenements/${encodeURIComponent(id)}`, { cache: "no-store" });
        const j = (await r.json()) as { evenement?: EvenementPublic; error?: string };
        if (!vivant) return;
        if (!r.ok || !j.evenement) {
          setErreur(j.error ?? "Événement introuvable.");
          return;
        }
        setE(j.evenement);
        setErreur(null);
      } catch {
        /* le réseau revient : on garde l'écran tel quel */
      }
    };
    void lire();
    const t = window.setInterval(() => void lire(), 4000);
    const h = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => {
      vivant = false;
      window.clearInterval(t);
      window.clearInterval(h);
    };
  }, [id]);

  useEffect(() => {
    document.title = e?.voix?.nom ? `${e.voix.nom} · écran central` : "Écran central";
  }, [e?.voix?.nom]);

  if (!e) {
    return (
      <div className="eh-central" style={{ justifyContent: "center", alignItems: "center" }}>
        <span className="eh-sous">{erreur ?? "…"}</span>
      </div>
    );
  }
  const accent = e.voix?.couleur || VIOLET;
  const marque = e.voix?.nom || e.nom;
  const tables = e.tables.slice().sort((a, b) => ordre(a) - ordre(b) || a.rdvAt - b.rdvAt || a.nom.localeCompare(b.nom, "fr", { numeric: true }));
  const adresse = `${location.host}/e/${e.id}`;

  return (
    <div
      className="eh-central"
      style={{ "--eh-accent": accent } as CSSProperties}
      onDoubleClick={() => {
        if (document.fullscreenElement) void document.exitFullscreen();
        else void document.documentElement.requestFullscreen().catch(() => undefined);
      }}
    >
      <header className="eh-haut">
        {e.voix?.image ? <img className="eh-logo" src={e.voix.image} alt="" /> : null}
        <div className="eh-marque">
          <b>{marque}</b>
          {e.voix?.nom && e.nom !== e.voix.nom ? <span>{e.nom}</span> : null}
        </div>
        <span className="eh-sp" />
        <span className="eh-heure">{heure(now)}</span>
      </header>

      <main className="eh-corps">
        <section className="eh-rejoindre">
          <span className="eh-tag">Rejoindre une table</span>
          <h1>
            Un sujet, quelques personnes,
            <br />
            <em>un rendez-vous ici.</em>
          </h1>
          <div className="eh-qr">
            <img src={`/api/evenements/${encodeURIComponent(e.id)}/qr.svg`} alt={`QR code : ${adresse}`} />
          </div>
          <span className="eh-sous">Scannez avec l'appareil photo. Sans application, sans compte.</span>
        </section>

        <section className="eh-tables" aria-label="Prochains rendez-vous">
          <div className="eh-entetes">
            <span>Heure</span>
            <span>Sujet</span>
            <span>Lieu</span>
            <span>Places</span>
          </div>
          {tables.length === 0 ? <p className="eh-vide">Les prochaines tables arrivent.</p> : null}
          {tables.map((t) => {
            const s = etat(t, now);
            return (
              <article key={t.sessionId} className={`eh-table${t.phase === "réunie" ? " passee" : ""}`}>
                <span className="eh-t-heure">{heure(t.rdvAt)}</span>
                <span className="eh-t-sujet">
                  <i style={{ background: t.couleur }} />
                  <b>{t.sujet || t.nom}</b>
                  {t.sujet && t.sujet !== t.nom ? <small>{t.nom}</small> : null}
                </span>
                <span className="eh-t-lieu">{t.point || "Sur le stand"}</span>
                <span className={`eh-t-etat${s.vif ? " vif" : ""}`}>{s.texte}</span>
              </article>
            );
          })}
        </section>
      </main>

      <footer className="eh-pied">
        <span>Les échanges restent privés. L'écran ne montre que les rendez-vous.</span>
        <span className="eh-sp" />
        <span className="eh-signature">
          espaces<b>humains</b>
        </span>
      </footer>
    </div>
  );
}
